import {
  ActionIcon,
  Button,
  Group,
  List,
  Modal,
  Stack,
  Text,
  ThemeIcon,
} from "@mantine/core";
import { showNotification, updateNotification } from "@mantine/notifications";
import { IconTrash } from "@tabler/icons-react";
import {
  getNotificationByResultType,
  loadingInfoNotification,
} from "beitary-shared";
import { useSubmitState } from "hooks/useSubmitState";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export interface DeleteConfirmationModalProps<T> {
  f: Function;
  item: T;
  button?: boolean;
  disabled?: boolean;
}

export const DeleteConfirmationModal = <
  T extends { id: string; name: string }
>({
  f,
  item,
  button,
  disabled,
}: DeleteConfirmationModalProps<T>) => {
  const { t } = useTranslation();
  const [submitState, setSubmitState] = useSubmitState();
  const [opened, setOpened] = useState(false);

  const deleteItem = async () => {
    const notificationId = `delete-item-${item.id}`;
    showNotification(
      loadingInfoNotification({
        id: notificationId,
        message: t("WAITING_FOR_SERVER_RESPONSE"),
        title: t("DELETE_ITEM"),
      })
    );
    setSubmitState("pending-response");
    const result = await f(item.id, item);
    setSubmitState(result.type);

    updateNotification({
      ...getNotificationByResultType(result.type)({
        message: t(result.message),
      }),
      id: notificationId,
    });
  };

  const confirm = () => {
    deleteItem();
    setOpened(false);
  };

  return (
    <>
      <Modal
        opened={opened}
        centered
        onClose={() => setOpened(false)}
        closeOnClickOutside={false}
        overlayProps={{
          color: "white",
          opacity: 0.55,
          blur: 3,
        }}
      >
        <Stack pb="xl" align="center" spacing={0}>
          <ThemeIcon size={140} color="red.4" radius={70} variant="light">
            <IconTrash size={100} />
          </ThemeIcon>
          <Text weight="bold" mt="xl">
            {t("YOU_ARE_ABOUT_TO_DELETE_AN_ITEM")}
          </Text>
          <List>
            <List.Item>{item.name}</List.Item>
          </List>
          <Text mt="xl">{t("THIS_ACTION_CANNOT_BE_UNDONE")}</Text>
          <Text>{t("ARE_YOU_SURE")}</Text>
        </Stack>
        <Group mt="xl" position="right">
          <Button color="red" onClick={confirm}>
            {t("DELETE")}
          </Button>
          <Button variant="outline" onClick={() => setOpened(false)}>
            {t("CANCEL")}
          </Button>
        </Group>
      </Modal>
      {button ? (
        <Button
          onClick={() => setOpened(true)}
          loading={submitState === "pending-response"}
          leftIcon={<IconTrash size={18} />}
          variant="outline"
          color="red"
          disabled={disabled}
        >
          {t("DELETE")}
        </Button>
      ) : (
        <ActionIcon
          disabled={disabled}
          onClick={() => setOpened(true)}
          loading={submitState === "pending-response"}
        >
          <IconTrash size={18} />
        </ActionIcon>
      )}
    </>
  );
};
